import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Drawer, Button, Space, Alert, message, Spin, Typography, Card, Descriptions, Switch, Select, Divider, Tag, Row, Col } from "antd";
import { MailOutlined, FileTextOutlined, EditOutlined, SendOutlined, SplitCellsOutlined, GlobalOutlined, EyeOutlined } from "@ant-design/icons";
import { scheduleService } from "../../../../api/enhancedApi";
import { emailTemplateApi } from "../../../../api/emailTemplateApi";
import { getPresetSenderAccounts } from "../../../../api/mailApi";
import { useAuth } from "../../../../hooks/useAuth";
import GeneralEmailEditor from "../../../../components/EmailEditor/GeneralEmailEditor";
import AircraftTag from "../../../../components/AircraftTag";
import { formatDate } from "../../../../utils/dateUtils";
// import { calculateAndSetActualGroundTimes } from "../../../../utils/timeUtils"; // 暂不需要：GT 显示问题真正根因是渲染器字段名不兼容，已在 ContentBlockRenderer.js 中修复
import { getInitialOptionValues, EmailType, SendingOptionType, isOptionEditable } from "../../../../config/emailTypeConfig";

const { Text, Title } = Typography;
const { Option } = Select;

/**
 * 统一邮件发送抽屉组件
 * 支持单个和批量计划的确认/发布邮件发送
 *
 * @param {boolean} visible - 抽屉是否可见
 * @param {function} onClose - 关闭抽屉的回调
 * @param {array} schedules - 选中的计划数组（单个或多个）
 * @param {string} emailType - 邮件类型：'confirmation' | 'publish'
 * @param {function} onSuccess - 发送成功的回调
 * @param {boolean} quickMode - 快速模式：跳过抽屉直接进入邮件编辑器
 * @param {string} defaultTemplateCode - 快速模式下使用的默认模板代码
 * @param {boolean} defaultSplitSending - 快速模式下默认的拆分发送设置
 */
const UnifiedEmailSendDrawer = ({ visible, onClose, schedules = [], emailType = "confirmation", onSuccess, quickMode = false, defaultTemplateCode = null }) => {
    const { userEmailConfig } = useAuth();
    // 基础状态
    const [loading, setLoading] = useState(false);
    const [schedulesWithFlights, setSchedulesWithFlights] = useState([]);
    const [isEmailEditorVisible, setIsEmailEditorVisible] = useState(false);
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [sendingProgress, setSendingProgress] = useState(null);

    // 发送选项状态 - 使用新的emailTypeConfig API
    const initialSendingOptions = getInitialOptionValues(emailType);
    const [sendingOptions, setSendingOptions] = useState(initialSendingOptions);

    // 模板相关状态
    const [templateLoading, setTemplateLoading] = useState(false);
    const [allTemplates, setAllTemplates] = useState({}); // 存储所有加载的模板详情
    const [selectedTemplateCode, setSelectedTemplateCode] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isTemplateInitialized, setIsTemplateInitialized] = useState(false); // 标记模板是否已初始化

    // 发件人相关状态
    const [senderOptions, setSenderOptions] = useState([]);
    const [selectedSender, setSelectedSender] = useState(null); // 强制使用的发件人

    // 根据邮件类型确定可用的模板
    const templateOptions = useMemo(() => {
        if (emailType === "confirmation") {
            return [
                {
                    code: "SCHEDULE_AAE_NEW_CONFIRMATION",
                    title: "AAE新发布计划确认",
                    icon: <FileTextOutlined style={{ color: "#1890ff" }} />,
                    isDefault: true,
                },
                {
                    code: "SCHEDULE_AAE_UPDATE_CONFIRMATION",
                    title: "AAE更新计划确认",
                    icon: <EditOutlined style={{ color: "#52c41a" }} />,
                    isDefault: false,
                },
            ];
        } else {
            return [
                {
                    code: "SCHEDULE_OUTSTATION_NEW_PUBLICATION",
                    title: "发布计划",
                    icon: <GlobalOutlined style={{ color: "#1890ff" }} />,
                    isDefault: true,
                },
                {
                    code: "SCHEDULE_OUTSTATION_UPDATE_PUBLICATION",
                    title: "更新计划",
                    icon: <EditOutlined style={{ color: "#fa8c16" }} />,
                    isDefault: false,
                },
            ];
        }
    }, [emailType]);

    // 是否为批量发送
    const isBatchSending = schedules.length > 1;

    // 计算基本统计信息
    const statistics = useMemo(() => {
        if (schedulesWithFlights.length === 0) return {};

        const aircraftSet = new Set();
        let totalFlights = 0;
        let dateRange = { start: null, end: null };

        schedulesWithFlights.forEach((schedule) => {
            if (schedule.aircraft?.registration) {
                aircraftSet.add(schedule.aircraft.registration);
            }
            totalFlights += schedule.flights?.length || 0;

            // 计算日期范围
            if (schedule.start_date) {
                const startDate = new Date(schedule.start_date);
                if (!dateRange.start || startDate < dateRange.start) {
                    dateRange.start = startDate;
                }
            }
            if (schedule.end_date) {
                const endDate = new Date(schedule.end_date);
                if (!dateRange.end || endDate > dateRange.end) {
                    dateRange.end = endDate;
                }
            }
        });

        return {
            aircraftCount: aircraftSet.size,
            aircraftList: Array.from(aircraftSet),
            totalFlights,
            dateRange,
        };
    }, [schedulesWithFlights]);

    // 加载所有选中计划的航班数据
    const loadSchedulesData = useCallback(async () => {
        if (!schedules || schedules.length === 0) return;

        try {
            setLoading(true);
            const schedulesData = [];

            // 并行加载所有计划的航班数据
            await Promise.all(
                schedules.map(async (schedule) => {
                    try {
                        const flightsResponse = await scheduleService.getScheduleFlights(schedule.id, {
                            page: 1,
                            limit: 1000,
                        });

                        // 确保flights始终是一个数组
                        let flightsData = [];
                        if (flightsResponse?.data?.items && Array.isArray(flightsResponse.data.items)) {
                            flightsData = flightsResponse.data.items;
                        } else if (flightsResponse?.data && Array.isArray(flightsResponse.data)) {
                            flightsData = flightsResponse.data;
                        } else if (flightsResponse?.items && Array.isArray(flightsResponse.items)) {
                            flightsData = flightsResponse.items;
                        } else if (Array.isArray(flightsResponse)) {
                            flightsData = flightsResponse;
                        } else {
                            console.warn("无法识别的API响应格式:", flightsResponse);
                            flightsData = [];
                        }

                        schedulesData.push({
                            ...schedule,
                            flights: flightsData,
                        });
                    } catch (error) {
                        console.error(`加载计划 ${schedule.id} 的航班数据失败:`, error);
                        // 即使加载失败，也添加到数组中，但flights为空数组
                        schedulesData.push({
                            ...schedule,
                            flights: [],
                            loadError: error.message || "加载航班数据失败",
                        });
                    }
                }),
            );

            // 按照ID排序，确保顺序一致
            schedulesData.sort((a, b) => a.id - b.id);
            setSchedulesWithFlights(schedulesData);
        } catch (error) {
            console.error("批量加载计划数据失败:", error);
            message.error("加载计划数据失败，请重试");
        } finally {
            setLoading(false);
        }
    }, [schedules]);

    // 批量加载所有模板详情
    const loadAllTemplates = useCallback(async () => {
        try {
            setTemplateLoading(true);
            const templateCodes = templateOptions.map((option) => option.code);

            // 并行加载所有模板
            const templatePromises = templateCodes.map(async (code) => {
                try {
                    const response = await emailTemplateApi.getTemplateByCodeWithRecipients(code);
                    return { code, data: response.data };
                } catch (error) {
                    console.error(`加载模板 ${code} 失败:`, error);
                    return { code, data: null, error };
                }
            });

            const results = await Promise.all(templatePromises);

            // 构建模板映射
            const templatesMap = {};
            results.forEach(({ code, data }) => {
                if (data) {
                    templatesMap[code] = data;
                }
            });

            setAllTemplates(templatesMap);
            console.log("[UnifiedEmailSendDrawer] 已批量加载所有模板:", templatesMap);
        } catch (error) {
            console.error("批量加载模板失败:", error);
            message.error("加载邮件模板失败，请重试");
        } finally {
            setTemplateLoading(false);
        }
    }, [templateOptions]);

    // 初始化默认模板选择（仅在组件首次加载时执行）
    const initializeDefaultTemplate = useCallback(() => {
        if (templateOptions.length > 0 && !isTemplateInitialized) {
            const defaultCode = quickMode && defaultTemplateCode ? defaultTemplateCode : templateOptions.find((option) => option.isDefault)?.code;

            if (defaultCode) {
                console.log("[UnifiedEmailSendDrawer] 初始化默认模板:", defaultCode);
                setSelectedTemplateCode(defaultCode);
                setIsTemplateInitialized(true);
            }
        }
    }, [templateOptions, quickMode, defaultTemplateCode, isTemplateInitialized]);

    // 处理模板切换
    const handleTemplateChange = useCallback(
        (templateCode) => {
            console.log("[UnifiedEmailSendDrawer] 切换模板:", templateCode);

            // 防止重复设置相同的模板
            if (selectedTemplateCode === templateCode) {
                console.log("[UnifiedEmailSendDrawer] 模板已选中，跳过重复设置");
                return;
            }

            setSelectedTemplateCode(templateCode);

            // 立即更新选中的模板详情
            if (allTemplates[templateCode]) {
                console.log("[UnifiedEmailSendDrawer] 立即更新模板详情:", templateCode);
                setSelectedTemplate(allTemplates[templateCode]);
            } else {
                console.warn("[UnifiedEmailSendDrawer] 模板详情未找到:", templateCode);
                setSelectedTemplate(null);
            }
        },
        [allTemplates, selectedTemplateCode],
    );

    // 打开邮件编辑器
    const handleOpenEmailEditor = useCallback(async () => {
        if (!selectedTemplate) {
            message.warning("请先选择邮件模板");
            return;
        }

        setIsEmailEditorVisible(true);
    }, [selectedTemplate]);

    // 组件初始化 - 当抽屉打开时加载数据
    useEffect(() => {
        if (visible && schedules?.length > 0) {
            console.log("[UnifiedEmailSendDrawer] 抽屉打开，开始加载数据");
            loadSchedulesData();
            loadAllTemplates();

            // 根据邮件类型配置设置发送选项的默认值
            setSendingOptions(initialSendingOptions);
        } else if (!visible) {
            // 抽屉关闭时清理数据
            console.log("[UnifiedEmailSendDrawer] 抽屉关闭，清理数据");
            setSchedulesWithFlights([]);
            setSelectedTemplateCode(null);
            setSelectedTemplate(null);
            setSendingOptions(initialSendingOptions); // 重置为初始值
            setAllTemplates({});
            setIsTemplateInitialized(false); // 重置初始化标记
        }
    }, [visible, schedules?.length, loadSchedulesData, loadAllTemplates]);

    // 加载预设发件账号
    useEffect(() => {
        if (visible) {
            getPresetSenderAccounts()
                .then((res) => {
                    if (res.success) {
                        const accounts = res.data || [];
                        // 强制使用 key 为 "schedule" 的账号
                        const scheduleAccount = accounts.find((acc) => acc.key === "schedule");
                        if (scheduleAccount) {
                            setSelectedSender(scheduleAccount);
                            // 只传递当前选中的账号，避免显示其他选项
                            setSenderOptions([scheduleAccount]);
                        } else {
                            message.error("未配置 'schedule' 预设发件账号，请联系管理员配置");
                            // 关闭抽屉，防止用户继续操作
                            onClose();
                        }
                    }
                })
                .catch((err) => {
                    console.error("加载预设发件账号失败:", err);
                    message.error("加载发件账号失败，请稍后重试");
                    onClose();
                });
        }
    }, [visible]);

    // 模板初始化 - 当模板选项和模板数据都加载完成后初始化默认模板
    useEffect(() => {
        if (!templateLoading && templateOptions.length > 0 && Object.keys(allTemplates).length > 0) {
            console.log("[UnifiedEmailSendDrawer] 模板数据加载完成，开始初始化默认模板");
            initializeDefaultTemplate();
        }
    }, [templateLoading, templateOptions.length, allTemplates, initializeDefaultTemplate]);

    // 模板详情更新 - 当选中的模板代码变化时，更新模板详情
    useEffect(() => {
        if (selectedTemplateCode && allTemplates[selectedTemplateCode] && isTemplateInitialized) {
            console.log("[UnifiedEmailSendDrawer] 更新选中模板详情:", selectedTemplateCode);
            setSelectedTemplate(allTemplates[selectedTemplateCode]);
        } else if (selectedTemplateCode && !allTemplates[selectedTemplateCode]) {
            console.warn("[UnifiedEmailSendDrawer] 选中的模板代码在allTemplates中不存在:", selectedTemplateCode, "可用模板:", Object.keys(allTemplates));
        }
    }, [selectedTemplateCode, allTemplates, isTemplateInitialized]);

    // 快速模式：当数据和模板都加载完成后，自动进入邮件编辑器
    useEffect(() => {
        if (quickMode && visible && !loading && !templateLoading && schedulesWithFlights.length > 0 && selectedTemplate && !isEmailEditorVisible) {
            console.log("[UnifiedEmailSendDrawer] 快速模式：自动进入邮件编辑器");
            setIsEmailEditorVisible(true);
        }
    }, [quickMode, visible, loading, templateLoading, schedulesWithFlights.length, selectedTemplate, isEmailEditorVisible]);

    // 处理邮件发送
    const handleEmailSubmit = async (emailData) => {
        try {
            setIsSending(true);
            setSendingProgress(`正在发送${emailType === "confirmation" ? "确认" : "发布"}邮件...`);

            // 转换前端数据格式为后端期望的格式
            const convertEmailField = (field) => {
                if (Array.isArray(field)) {
                    return field.length > 0 ? field.join(", ") : null;
                }
                return field || null;
            };

            // 构建请求数据
            // 注意：后端 ScheduleConfirmationEmailRequest 期望的字段名与前端 emailTypeConfig 不同
            // 前端: bcc_sending, read_receipt
            // 后端: individual_email_per_recipient, request_read_receipt
            const requestData = {
                to: convertEmailField(emailData.to) || "",
                cc: convertEmailField(emailData.cc),
                bcc: convertEmailField(emailData.bcc),
                subject: emailData.subject || "",
                rendered_html: emailData.contentHtml || "",
                content_block_context: emailData.contentBlockContext || {
                    template_code: selectedTemplateCode,
                    instances: [],
                    business_context_snapshot: {},
                },
                attachments: emailData.attachments || [],
                // 转换为后端期望的参数名
                individual_email_per_recipient: sendingOptions[SendingOptionType.BCC_SENDING] || false,
                request_read_receipt: sendingOptions[SendingOptionType.READ_RECEIPT] || false,
                preset_sender_key: emailData.selectedSender?.key || null, // 传递预设发件账号标识
            };

            // 根据发送模式更新进度信息
            if (sendingOptions[SendingOptionType.BCC_SENDING]) {
                setSendingProgress(`正在启动群发单显模式...`);
            } else {
                setSendingProgress(`正在发送邮件...`);
            }

            let response;

            if (isBatchSending) {
                // 批量发送
                requestData.schedule_ids = schedulesWithFlights.map((schedule) => schedule.id);
                setSendingProgress(`正在批量发送${emailType === "confirmation" ? "确认" : "发布"}邮件...`);

                if (emailType === "confirmation") {
                    const { sendBatchScheduleConfirmationEmailV2 } = await import("../../../../api/scheduleEmailApi");
                    response = await sendBatchScheduleConfirmationEmailV2(requestData);
                } else {
                    const { sendBatchSchedulePublishEmailV2 } = await import("../../../../api/scheduleEmailApi");
                    response = await sendBatchSchedulePublishEmailV2(requestData);
                }
            } else {
                // 单个发送
                const scheduleId = schedulesWithFlights[0]?.id;
                if (!scheduleId) {
                    throw new Error("无效的计划ID");
                }

                setSendingProgress(`正在发送${emailType === "confirmation" ? "确认" : "发布"}邮件...`);

                if (emailType === "confirmation") {
                    const { sendScheduleConfirmationEmailV2 } = await import("../../../../api/scheduleEmailApi");
                    response = await sendScheduleConfirmationEmailV2(scheduleId, requestData);
                } else {
                    const { sendSchedulePublishEmailV2 } = await import("../../../../api/scheduleEmailApi");
                    response = await sendSchedulePublishEmailV2(scheduleId, requestData);
                }
            }

            if (response.success) {
                // 根据是否为异步发送显示不同的成功消息
                let successMessage;
                if (sendingOptions[SendingOptionType.BCC_SENDING]) {
                    // 异步发送模式
                    successMessage = response.message || `${emailType === "confirmation" ? "确认" : "发布"}邮件已启动异步发送，正在后台处理`;
                    // 显示更详细的异步发送信息，并引导用户查看发件箱
                    message.success({
                        content: (
                            <div>
                                <div style={{ fontWeight: "bold", marginBottom: 4 }}>邮件发送任务已启动</div>
                                <div style={{ fontSize: "12px", color: "#666" }}>{successMessage}</div>
                                <div style={{ fontSize: "12px", color: "#1890ff", marginTop: 4 }}>由于开启了"群发单显"，系统将在后台逐一发送邮件</div>
                                <div style={{ fontSize: "12px", color: "#52c41a", marginTop: 4 }}>可前往「发件箱」查看发送进度和结果</div>
                            </div>
                        ),
                        duration: 8, // 异步模式下显示更长时间，让用户有时间阅读
                    });
                } else {
                    // 同步发送模式
                    successMessage = response.message || `${emailType === "confirmation" ? "确认" : "发布"}邮件发送成功`;
                    message.success({
                        content: (
                            <div>
                                <div style={{ fontWeight: "bold", marginBottom: 4 }}>邮件发送成功</div>
                                <div style={{ fontSize: "12px", color: "#666" }}>{successMessage}</div>
                            </div>
                        ),
                        duration: 4,
                    });
                }

                setIsEmailEditorVisible(false);
                onSuccess && onSuccess();
            } else {
                message.error(response.message || "邮件发送失败");
            }
        } catch (error) {
            console.error("发送邮件失败:", error);

            // 安全地提取错误消息
            let errorMessage = "发送邮件时发生错误";

            if (error.response?.data?.detail) {
                if (Array.isArray(error.response.data.detail)) {
                    const validationErrors = error.response.data.detail
                        .map((err) => {
                            const field = err.loc ? err.loc.join(".") : "未知字段";
                            return `${field}: ${err.msg}`;
                        })
                        .join("; ");
                    errorMessage = `数据验证失败: ${validationErrors}`;
                } else if (typeof error.response.data.detail === "string") {
                    errorMessage = error.response.data.detail;
                }
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            message.error({
                content: (
                    <div>
                        <div style={{ fontWeight: "bold", marginBottom: 4 }}>❌ 邮件发送失败</div>
                        <div style={{ fontSize: "12px", color: "#666" }}>{errorMessage}</div>
                    </div>
                ),
                duration: 6,
            });
        } finally {
            setIsSending(false);
            setSendingProgress(null);
            setIsEmailLoading(false);
        }
    };

    // 关闭邮件编辑器
    const handleEmailCancel = () => {
        setIsEmailEditorVisible(false);
        // 快速模式下，关闭邮件编辑器就等于关闭整个组件
        if (quickMode) {
            onClose();
        }
    };

    // 生成邮件上下文数据 - 支持 V3 动态内容块
    const generateEmailContextData = useCallback(() => {
        if (schedulesWithFlights.length === 0) return {};

        if (isBatchSending) {
            // 批量发送：使用 V3 动态内容块模式

            // 获取所有飞机注册号
            const aircraftRegistrations = [...new Set(schedulesWithFlights.map((schedule) => schedule.aircraft?.registration).filter(Boolean))];

            // 获取计划的日期范围 - 取第一个计划的日期范围即可
            const firstSchedule = schedulesWithFlights[0];
            const dateRange = {
                start_date: firstSchedule.start_date,
                end_date: firstSchedule.end_date,
            };

            // 为邮件主题准备飞机注册号文本
            const aircraftText = aircraftRegistrations.join(" & ");

            // 为邮件主题准备日期范围文本 (例如: 16JUN-19JUN)
            const startDate = new Date(dateRange.start_date);
            const endDate = new Date(dateRange.end_date);

            const formatDateForSubject = (date) => {
                const day = date.getDate().toString().padStart(2, "0");
                const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
                return `${day}${month}`;
            };

            const dateRangeText = `${formatDateForSubject(startDate)}-${formatDateForSubject(endDate)}`;

            // V3 新增：使用 dataSources 格式为多个计划创建统一的内容块
            // 将每个计划的航班数据作为独立的数据源
            const flightListDataSources = schedulesWithFlights.map((schedule) => ({
                // 每个数据源包含该计划的航班列表和计划信息
                flights: schedule.flights || [],
                scheduleInfo: {
                    id: schedule.id,
                    name: schedule.name,
                    aircraft_registration: schedule.aircraft?.registration,
                    start_date: schedule.start_date,
                    end_date: schedule.end_date,
                },
            }));

            return {
                // V1模式兼容格式（保留原有结构）
                schedules: schedulesWithFlights,

                // V3模式需要的contentBlocks格式 - 动态扩展
                contentBlocks: [
                    {
                        id: "flight_list", // 统一使用 flight_list 作为ID，匹配模板
                        type: "scheduleFlightList",
                        title: "航班列表",
                        // 使用 dataSources 触发动态扩展
                        dataSources: flightListDataSources,
                        // 为行内显示准备的文本
                        inlineText: `${aircraftText} on ${dateRangeText}`,
                        business_source: {
                            entity_type: "ScheduleFlight",
                            entity_ids: schedulesWithFlights.flatMap((schedule) => (schedule.flights || []).filter((flight) => flight.id).map((flight) => flight.id)),
                        },
                        // 添加统计信息用于主题模板
                        statistics: statistics,
                    },
                ],
            };
        } else {
            // 单个发送：使用标准模式
            const schedule = schedulesWithFlights[0];
            return {
                // V1模式兼容格式
                schedule: schedule,
                flights: schedule?.flights || [],

                // V2模式需要的contentBlocks格式
                contentBlocks: [
                    {
                        id: "flight_list", // 统一使用 flight_list 作为ID，匹配模板
                        type: "scheduleFlightList",
                        title: "计划航班列表",
                        data: schedule?.flights || [],
                        business_source: {
                            entity_type: "ScheduleFlight",
                            entity_ids: (schedule?.flights || []).filter((flight) => flight.id).map((flight) => flight.id),
                        },
                        schedule_info: schedule,
                    },
                ],
            };
        }
    }, [schedulesWithFlights, isBatchSending, statistics]);

    // 渲染选中的计划列表
    const renderSelectedSchedules = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 16 }}>正在加载计划数据...</p>
                </div>
            );
        }

        if (schedulesWithFlights.length === 0) {
            return <Alert message="无计划数据" description="未找到有效的计划数据，请检查选择的计划。" type="warning" showIcon />;
        }

        return (
            <div>
                {/* 计划列表 */}
                <div style={{ maxHeight: 150, overflowY: "auto" }}>
                    {schedulesWithFlights.map((schedule, index) => (
                        <Card key={schedule.id} size="small" style={{ marginBottom: 8 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div>
                                    {schedule.aircraft?.registration && <AircraftTag registration={schedule.aircraft.registration} />}
                                    <Text strong style={{ marginLeft: 8 }}>
                                        {schedule.name || `计划 ${schedule.id}`}
                                    </Text>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <Text type="secondary">{schedule.flights?.length || 0} 个航班</Text>
                                    <Text type="secondary" style={{ marginLeft: 8 }}>
                                        {schedule.start_date && schedule.end_date ? `${formatDate(schedule.start_date)} 至 ${formatDate(schedule.end_date)}` : "无日期范围"}
                                    </Text>

                                    {schedule.loadError && (
                                        <Tag color="red" style={{ marginLeft: 8 }}>
                                            加载失败
                                        </Tag>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        );
    };

    // 渲染模板选择
    const renderTemplateSelection = () => {
        return (
            <div>
                <Row gutter={[12, 12]}>
                    {templateOptions.map((template) => (
                        <Col span={12} key={template.code}>
                            <Card
                                size="small"
                                hoverable
                                style={{
                                    cursor: "pointer",
                                    minHeight: "8em",
                                    border: selectedTemplateCode === template.code ? "2px solid #1890ff" : "1px solid #d9d9d9",
                                    backgroundColor: selectedTemplateCode === template.code ? "#f0f8ff" : "#fff",
                                }}
                                onClick={() => handleTemplateChange(template.code)}
                                bodyStyle={{ padding: 12 }}
                            >
                                <div style={{ display: "flex", alignItems: "flex-start" }}>
                                    <div style={{ fontSize: 20, marginRight: 8, marginTop: 2 }}>{template.icon}</div>
                                    <div style={{ flex: 1 }}>
                                        <div
                                            style={{
                                                fontWeight: selectedTemplateCode === template.code ? "bold" : "normal",
                                                color: selectedTemplateCode === template.code ? "#1890ff" : "#000",
                                                fontSize: 14,
                                                marginBottom: 4,
                                            }}
                                        >
                                            {template.title}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: "#666",
                                                lineHeight: "1.4",
                                                whiteSpace: "pre-line",
                                            }}
                                        >
                                            {templateLoading
                                                ? "加载中..."
                                                : allTemplates[template.code]?.body_text
                                                  ? allTemplates[template.code].body_text
                                                        .split("\n")
                                                        .filter((line) => line.trim())
                                                        .slice(0, 2)
                                                        .join("\n") + "..."
                                                  : "暂无内容预览"}
                                        </div>
                                    </div>
                                </div>
                                {selectedTemplateCode === template.code && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            color: "#1890ff",
                                            fontSize: 16,
                                        }}
                                    >
                                        ✓
                                    </div>
                                )}
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
        );
    };

    const drawerTitle = useMemo(() => {
        const typeText = emailType === "confirmation" ? "确认" : "发布";
        const countText = isBatchSending ? `批量发送${typeText}邮件 (${schedules.length}个计划)` : `发送${typeText}邮件`;
        return countText;
    }, [emailType, isBatchSending, schedules.length]);

    return (
        <>
            {/* 快速模式下不显示抽屉 */}
            {!quickMode && (
                <Drawer title={drawerTitle} placement="right" width="50%" onClose={onClose} open={visible} destroyOnHidden={true}>
                    <div className="unified-email-send-content">
                        {/* 选中的计划 */}
                        <div style={{ marginBottom: 20 }}>
                            <Title level={5} style={{ marginBottom: 12, color: "#1890ff" }}>
                                选中的计划
                            </Title>
                            {renderSelectedSchedules()}
                        </div>

                        <Divider style={{ margin: "16px 0" }} />

                        {/* 模板选择 */}
                        <div style={{ marginBottom: 20 }}>
                            <Title level={5} style={{ marginBottom: 12, color: "#1890ff" }}>
                                邮件模板
                            </Title>
                            {templateLoading ? (
                                <div style={{ textAlign: "center", padding: "40px 0" }}>
                                    <Spin size="large" />
                                    <div style={{ marginTop: 12, color: "#666" }}>正在加载模板内容...</div>
                                </div>
                            ) : (
                                renderTemplateSelection()
                            )}
                        </div>

                        <Divider style={{ margin: "16px 0" }} />

                        {/* 操作按钮 */}
                        <div style={{ textAlign: "center", paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
                            <Space size="large">
                                <Button onClick={onClose} disabled={isSending}>
                                    取消
                                </Button>
                                <Button type="primary" icon={<SendOutlined />} onClick={handleOpenEmailEditor} loading={isEmailLoading || isSending} disabled={!selectedTemplateCode || schedulesWithFlights.length === 0 || isSending}>
                                    {isSending ? "正在发送..." : "打开邮件编辑器"}
                                </Button>
                            </Space>
                            {/* 发送进度显示 */}
                            {sendingProgress && (
                                <div style={{ marginTop: 12, color: "#1890ff", fontSize: "12px" }}>
                                    <Spin size="small" style={{ marginRight: 8 }} />
                                    {sendingProgress}
                                </div>
                            )}
                        </div>
                    </div>
                </Drawer>
            )}

            {/* 邮件编辑器 */}
            {isEmailEditorVisible && selectedTemplate && (
                <GeneralEmailEditor
                    visible={isEmailEditorVisible}
                    senderOptions={senderOptions}
                    defaultSender={selectedSender}
                    senderEditable={false}
                    emailType={emailType}
                    sendingOptions={sendingOptions}
                    onSendingOptionsChange={setSendingOptions}
                    onSubmit={handleEmailSubmit}
                    onCancel={handleEmailCancel}
                    title={`发送${emailType === "confirmation" ? "确认" : "发布"}邮件`}
                    subjectTemplate={selectedTemplate?.subject_template || ""}
                    initialSubject={`${emailType === "confirmation" ? "航班计划确认" : "航班计划发布"} - ${statistics.aircraftList?.join(" & ") || "未知飞机"}`}
                    templateContent={selectedTemplate?.body_html || ""}
                    templateRecipients={{
                        default_to: selectedTemplate?.resolved_to || [],
                        default_cc: selectedTemplate?.resolved_cc || [],
                    }}
                    businessContext={generateEmailContextData()}
                    enableV2Mode={true}
                    // 传递发送状态给邮件编辑器
                    loading={isSending}
                    loadingText={sendingProgress}
                    // 发送过程中禁用取消操作
                    cancelDisabled={isSending}
                />
            )}
        </>
    );
};

export default UnifiedEmailSendDrawer;
